import { Button, InfoSheet, Row, useInfoSheet } from '@fs/zion-ui'
import { ArrowCaret } from '@fs/zion-icon'
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import ZionForm, { useZionForm } from '@fs/zion-form'
import uniqBy from 'lodash.uniqby'
import zionDebug from '@fs/zion-debug'
import { useFeatureFlags } from '@fs/zion-flags'
import { useQueryParams } from '@fs/zion-router'
import {
  addAppAttribution,
  convertToSLSRectangle,
  generateSls,
  useSlsDispatchContext,
  useSlsHighlightsContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import ReviewStep1 from './ReviewStep1'
import ReviewStep2 from './ReviewStep2'
import ReviewStep3 from './ReviewStep3'
import ReviewStep4, { validateStep4 } from './ReviewStep4'
import { useFullNameDispatchContext, useFullNameStateContext } from '../../providers/FullNameContext'
import { errorCollector } from '../../../../../shared/utils/utils'
import assembleDispatches from '../../utils/saveFunctions'
import { FullNameApp } from '../../../../../shared/services/config'
import {
  patronCompletedTaskStats,
  saveGetInvolvedStats,
  saveTaskEdits,
} from '../../../../../shared/services/recordEnhancement/recordEnhancementService'
import {
  confirmThatChangesHaveBeenMade,
  nameFieldTypes,
  updateCurrentNameParts,
} from '../../utils/fullNameCorrectionUtils'
import { INFINITE_TASKS_GOAL } from '../../../../hooks/useTasks'

export const prepareStep4Data = ({ currentNameParts = {} }) => {
  const step4Data = {}
  const nameKeys = Object.keys(currentNameParts)

  nameKeys.forEach((nameKey) => {
    let text = ''
    let originalValue = ''
    const highlightIds = []
    currentNameParts[nameKey].fields.forEach((field) => {
      text += `${field.userEnteredValue} `
      originalValue = field?.originalValue
      highlightIds.push(field.id)
    })
    step4Data[nameKey] = {
      text: text.trim(),
      originalValue,
      highlightIds,
    }
  })

  return step4Data
}

/**
 * Initializes the form values for the review steps.
 *
 * @param {Object} [originalData={}] - The original data for the task.
 * @returns {Object} - The initial values for the form.
 */
const initialValues = (originalData = {}) => ({
  step1: {
    originalData, // This stores the original data for the task in case the user wants to go back to step 1
  },
  step4: prepareStep4Data({ currentNameParts: originalData }), // Dynamically set in the prepareStep4 function
})

const debug = zionDebug('rip:FullNameCorrection') // to turn this on do this in the console: localStorage.debug = 'fs:rip:FullNameCorrection'

const ReviewPanel = ({
  stepper,
  types,
  useTasksMethods,
  finishedOverlay,
  generateTasks,
  setError,
  handleSkip,
  resetSkipCounter,
}) => {
  const [t] = useTranslation()
  const { query } = useQueryParams()
  const { task: taskTypeParam } = query
  const state = useFullNameStateContext()
  const dispatch = useFullNameDispatchContext()
  const zionSlsState = useSlsStateContext()
  const zionSlsDispatch = useSlsDispatchContext()
  const infoSheetOverlay = useInfoSheet({ initiallyOpen: true })
  const [fullNamedHighlighted, setFullNamedHighlighted] = useState(false)
  const [taskRegenerateTriggered, triggerTaskRegeneration] = useState(false)
  const { currentTask, currentIndex, goal, doneCount, goToPreviousTask, goToNextTask, updateTaskList } = useTasksMethods
  const h = useSlsHighlightsContext()
  const { rip_sharedImageViewer_saveChanges: saveChangesFlag, rip_sharedImageViewer_statsTracking: statsTracking } =
    useFeatureFlags(['rip_sharedImageViewer_saveChanges', 'rip_sharedImageViewer_statsTracking'])

  // we create this maxDoneCountRef value because the doneCount value is returning to the previews value with goToPreviousTask.
  const maxDoneCountRef = useRef(currentIndex)
  if (currentIndex > maxDoneCountRef.current) {
    maxDoneCountRef.current = currentIndex
  }

  // The "Next" button should be enabled if:
  // - The user hasn't reached their goal (doneCount + 1 < goal)
  // - OR the goal is 0 (unlimited tasks), which means the user can always continue
  // - AND the user is not at the last available task (currentIndex < maxDoneCountRef.current)
  // This fixes the issue where the Next button would not activate for unlimited tasks (goal === 0)
  const showNextFirstStep = (doneCount + 1 < goal || goal === 0) && currentIndex < maxDoneCountRef.current

  const { currentStep } = stepper

  const formMethods = useZionForm({
    mode: 'all',
    defaultValues: initialValues(state.currentNameParts),
    resolver: async (values) => validateStep4(values, t),
  })

  /**
   * Saves the current state to the SLS backend.
   *
   * @param {Object} params - Parameters object.
   * @param {Array} params.dispatches - Array of SLS dispatches.
   * @param {Boolean} params.updateState - A flag to allow the update of the local zionSls state.
   * @returns {Promise} - A promise that resolves when the save operation is complete.
   */
  const saveToSls = useCallback(
    async (dispatches, updateState = true) => {
      const sls = generateSls(dispatches)
      addAppAttribution(sls, state.userId, FullNameApp)

      if (saveChangesFlag.isOn) {
        if (sls?.length) {
          debug('saveSls: ', { sls })
          await saveTaskEdits(zionSlsState.currentImageArk, sls, false, false, taskTypeParam)
        }
      } else {
        debug('Saving changes to SLS is disabled')
      }

      if (updateState) {
        zionSlsDispatch({
          type: 'BATCH_DISPATCHES',
          value: dispatches,
        })
      }
    },
    [saveChangesFlag.isOn, state.userId, taskTypeParam, zionSlsState.currentImageArk, zionSlsDispatch]
  )

  /**
   * Handles the form submission. This is called when the done button is clicked on step 4
   *
   * @param {Object} values - The form values. Automaticcally passed by the form handleSubmit function.
   */
  const submitHandler = async (values) => {
    const { step4 } = values
    const nameValues = {}

    nameFieldTypes.forEach((nameKey) => {
      if (step4[nameKey]) {
        const highlightIds = step4[nameKey].highlightIds
        const { mainEditHighlights } = h.getHighlights()

        nameValues[nameKey] = {
          originalValue: step4[nameKey]?.originalValue,
          text: step4[nameKey].text,
          rects: highlightIds.map((highlightId) => {
            return convertToSLSRectangle(mainEditHighlights.find((hl) => hl.id === highlightId))
          }),
          fieldIds: uniqBy(state.currentNameParts[nameKey].fields.map((f) => f.fieldId)),
        }
      } else {
        nameValues[nameKey] = {
          originalValue: '',
          text: '',
          rects: [],
          fieldIds: [],
        }
      }
    })

    // Check for either a surname or given name (otherwise the save endpoint will err out)
    try {
      if (!nameValues.NAME_SURN?.text && !nameValues.NAME_GN?.text) {
        throw new Error('No name values')
      }
    } catch (err) {
      errorCollector(err)
      setError(true)
      return
    }

    const { dispatches, removeDispatches } = assembleDispatches(
      nameValues,
      currentTask,
      zionSlsState.images,
      saveChangesFlag.isOn
    )

    try {
      // We have to send two separate transactions to prevent fields with the same rects from being deduped by SLS.
      await saveToSls(removeDispatches)
      await saveToSls(dispatches)

      if (statsTracking.isOn) {
        const nameValuesAreDifferent = confirmThatChangesHaveBeenMade({ nameValues, images: zionSlsState?.images })
        // We have to send this every time a name is reviewed
        saveGetInvolvedStats({
          partitionKey: currentTask?.partitionKey,
          taskType: 'Full Name Review',
          action: state.userPerformedFix || nameValuesAreDifferent ? 'fixed' : 'reviewed',
        })
      } else {
        patronCompletedTaskStats(currentTask.partitionKey, 'Full Name Review')
      }

      // Either increase the doneCount and index or open the finished overlay
      if (goal === INFINITE_TASKS_GOAL || doneCount + 1 < goal) {
        goToNextTask()
        triggerTaskRegeneration(true) // This will trigger the useEffect to regenerate the tasks with the updated data
        stepper.goToStep(1)
      } else {
        finishedOverlay.open()
      }
    } catch (err) {
      errorCollector(err, currentTask?.partitionKey, {
        codeId: 70,
        removeDispatches,
        dispatches,
        error: err,
      })
      setError(err)
    }
  }

  // This is the back button handler when going back from step 2 to step 1
  const backToStep1 = useCallback(() => {
    h.setCreatingRects(false)
    h.clearEdits()

    const originalData = formMethods.getValues('step1.originalData')
    dispatch({ type: 'SET_CURRENT_NAME_PARTS', value: originalData }) // Reset the currentNameParts to the original data
    stepper.goToStep(1)
  }, [stepper, dispatch, formMethods, h])

  // This function will prepare the data when going from step 2 to step 3
  const prepareStep3 = useCallback(() => {
    h.setCreatingRects(false)

    const { mainEditHighlights } = h.stageEdits()
    const attachedHighlights = mainEditHighlights.filter((hl) => hl.attached)
    h.setDisplayHighlights({ mainPartitionKey: currentTask.partitionKey, highlights: attachedHighlights })

    updateCurrentNameParts({
      mainHighlights: mainEditHighlights,
      state,
      zionSlsState,
      t,
      dispatch,
    })
    stepper.nextStep()
  }, [stepper, currentTask?.partitionKey, h, dispatch, state, zionSlsState, t])

  // This function will prepare the data when going from step 3 to step 4
  const prepareStep4 = useCallback(() => {
    formMethods.setValue('step4', prepareStep4Data({ currentNameParts: state.currentNameParts }), {
      shouldDirty: true,
      shouldTouch: true,
    })
    formMethods.trigger()

    stepper.nextStep()
  }, [stepper, formMethods, state.currentNameParts])

  // Based on the current step, render the correct view in the panel
  const stepToRender = useMemo(() => {
    switch (currentStep) {
      case 1: {
        return (
          <ReviewStep1
            useTasksMethods={useTasksMethods}
            stepper={stepper}
            setFullNamedHighlighted={setFullNamedHighlighted}
            triggerTaskRegeneration={triggerTaskRegeneration}
            setError={setError}
            handleSkip={handleSkip}
            resetSkipCounter={resetSkipCounter}
          />
        )
      }
      case 2:
        return <ReviewStep2 useTasksMethods={useTasksMethods} />
      case 3:
        return <ReviewStep3 currentTask={currentTask} types={types} />
      case 4:
        return <ReviewStep4 types={types} />
      default:
        return null
    }
  }, [currentStep, useTasksMethods, currentTask, types, stepper, setError, handleSkip, resetSkipCounter])

  // This will determine the actions for the footer buttons based on the current step
  const footerActions = useMemo(() => {
    switch (currentStep) {
      case 2:
        return {
          back: backToStep1,
          next: prepareStep3,
        }
      case 3:
        return {
          next: prepareStep4,
        }
      default:
        return {
          next: stepper.nextStep,
        }
    }
  }, [currentStep, prepareStep3, prepareStep4, backToStep1, stepper])

  // This will handle the back button when the user is on step 3 and the full name is highlighted
  const handlBackButton = useCallback(() => {
    if (fullNamedHighlighted && stepper.currentStep === 3) {
      stepper.goToStep(1)
    } else {
      stepper.prevStep()
    }
  }, [fullNamedHighlighted, stepper])

  // THIS IS SUPER IMPORTANT, it regenerates the tasks when the taskRegenerateTriggered state is set to true so
  // that the user can move on to the next task with the updated data
  useEffect(() => {
    if (taskRegenerateTriggered) {
      const newTaskList = generateTasks({ images: zionSlsState.images, currentIndex })
      updateTaskList({ newTaskList }) // This does no navigation, it only updates the array of tasks
      triggerTaskRegeneration(false)
    }
  }, [
    generateTasks,
    taskRegenerateTriggered,
    zionSlsState.images,
    updateTaskList,
    currentIndex,
    dispatch,
    triggerTaskRegeneration,
  ])

  // This will update the form to have the correct values when the task changes
  useEffect(() => {
    formMethods.reset(initialValues(state.currentNameParts))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTask, formMethods])

  const firstStepButtons = (
    <Row alignX="end" gutters="md">
      <Button Icon={ArrowCaret} iconDirection="backward" onClick={goToPreviousTask} disabled={doneCount <= 0}>
        {t('edit-shared-surname-footer.back', 'Back')}
      </Button>
      <Button emphasis="medium" onClick={goToNextTask} disabled={!showNextFirstStep}>
        {t('edit-shared-surname-footer.next', 'Next')}
      </Button>
    </Row>
  )

  return (
    <ZionForm methods={formMethods} id={currentTask?.id || 'full-name-correction'}>
      <InfoSheet
        {...infoSheetOverlay}
        size="md"
        title={t('full-name-correction.infosheet-header', 'Full Name Review')}
        subtitle={t('full-name-correction.stepXof4', 'Step {step} of 4', { step: currentStep })}
        headingColor="purple"
        nonClosable
        footer={
          stepper.isFirstStep ? (
            firstStepButtons
          ) : (
            <Row alignX="end">
              <Button onClick={footerActions?.back || handlBackButton}>{t('navigation.back-button', 'Back')}</Button>
              {stepper.isLastStep ? (
                <Button emphasis="high" onClick={formMethods.handleSubmit(submitHandler)}>
                  {t('navigation.done-button', 'Done')}
                </Button>
              ) : (
                <Button emphasis="high" onClick={footerActions.next}>
                  {t('navigation.next-button', 'Next')}
                </Button>
              )}
            </Row>
          )
        }
      >
        {stepToRender}
      </InfoSheet>
    </ZionForm>
  )
}

export default ReviewPanel
